import React, { useRef } from 'react';
import { FormRef } from '@lib/components/ReactHookForm/types';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import useModalManagement from 'routes/FormTemplate/utils/useModalManagement';
import useActions from 'routes/FormTemplate/utils/useActions';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import { useEdmDocumentationsLightQuery } from '@lib/graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import TabCountersContext from '@lib/contexts/TabCountersContext';
import useTabCounters from '@lib/hooks/useTabCounters';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import FormTemplateBuilderViewMenu from 'components/FormTemplateBuilder/FormTemplateBuilderView/FormTemplateBuilderViewMenu';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { FormTemplateMode } from './enums';
import FormTemplateDetailsViewContainer from './FormTemplateDetailsViewContainer';
import FormTemplateDocumentsViewContainer from './FormTemplateDocumentsViewContainer';
import FormTemplateTasksViewContainer from './FormTemplateTasksViewContainer';

interface Props {
  viewMode: FormTemplateMode;
}

export default function FormTemplateViewContainer(props: Props) {
  const { viewMode } = props;
  const formRef = useRef<FormRef>(undefined);
  const { id = '', versionId = '' } = useParams();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const { getUserAccessByCompany, getUserAccessByComponent }: ContextProps =
    useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) ===
    AccessLevelEnum.Read;
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const useModalManagementResult = useModalManagement();

  const useActionsResult = useActions({
    useModalManagementResult,
    formRef,
    id,
    isViewLocked,
    versionId,
    viewMode,
  });
  const { formTemplateData, formTemplateError, formTemplateRefetch } =
    useActionsResult;

  // Lightweight query to get linked documents count for tab counter.
  // `linkedFormTemplatesId` filter arg is gated by `documentation_management`
  // on the BE — skip entirely for LimitedRead users.
  const { data: linkedDocsData, refetch: refetchLinkedDocsCount } =
    useEdmDocumentationsLightQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        page: 1,
        limit: 1,
        filters: [
          {
            linkedFormTemplatesId: {
              predicate: { eq: id },
            },
          },
        ],
      },
      skip: !id || isViewLocked,
    });
  const linkedDocumentsCount = _get(
    linkedDocsData,
    'data.metadata.totalCount',
    0,
  ) as number;

  // The two tab counters come from two different queries — linked documents
  // from the light query above, linked tasks from `formTemplateData` — so both
  // are refreshed together. Tab containers reach this through
  // `useTabCountersContext()`; the shell also refreshes on every tab switch,
  // which is what keeps the tasks counter honest after work done in the other
  // tab.
  const tabCounters = useTabCounters({
    refetch: () =>
      Promise.all([
        // `refetch` bypasses `skip`, so the LimitedRead guard has to be
        // repeated here or the gated filter arg would hit the BE anyway.
        isViewLocked ? undefined : refetchLinkedDocsCount(),
        formTemplateRefetch?.(),
      ]),
    skip: !id,
  });

  let content: React.ReactNode = null;

  if (viewMode === FormTemplateMode.View) {
    content = (
      <FormTemplateDetailsViewContainer
        useActionsResult={useActionsResult}
        useModalManagementResult={useModalManagementResult}
        viewMode={viewMode}
        isViewOnly={isViewOnly}
      />
    );
  } else if (viewMode === FormTemplateMode.ViewLinkedTasks) {
    content = (
      <FormTemplateTasksViewContainer
        useActionsResult={useActionsResult}
        useModalManagementResult={useModalManagementResult}
        isViewOnly={isViewOnly}
      />
    );
  } else if (viewMode === FormTemplateMode.ViewLinkedDocuments) {
    content = (
      <FormTemplateDocumentsViewContainer
        useActionsResult={useActionsResult}
        useModalManagementResult={useModalManagementResult}
        isViewOnly={isViewOnly}
      />
    );
  }

  if (!content) return null;

  const breadCrumbsData = getBreadCrumbsDataByKey({
    entity: {
      versionId: formTemplateData?.versionId || versionId,
      id: formTemplateData?.id || id,
      name: formTemplateData?.name,
    },
    pathname,
    t,
  });

  // Only the details and linked-tasks views used to render an ErrorPage (each
  // did it internally). `formTemplateError` also folds in the *version* query's
  // error, and the linked-documents route carries a `:versionId` — so gating
  // that view too would blank an otherwise working documents table whenever the
  // version fails to load. Errors there stay surfaced via the GraphQL toast.
  if (formTemplateError && viewMode !== FormTemplateMode.ViewLinkedDocuments) {
    return (
      <>
        <BreadCrumbs data={breadCrumbsData} />
        <ErrorPage
          status={getStatusFromError(formTemplateError)}
          description={getDescriptionFromError(formTemplateError)}
        />
      </>
    );
  }

  // The tab bar is rendered here — above the per-mode containers rather than
  // inside each of them — so switching tabs swaps only the content below it.
  // Rendering it per mode would mount a fresh TabBar on every tab click, and
  // its sliding active indicator can't animate across a remount.
  return (
    <TabCountersContext.Provider value={tabCounters}>
      <BreadCrumbs data={breadCrumbsData} />
      <PageBody withoutHeader>
        <FormTemplateBuilderViewMenu
          formTemplateData={formTemplateData}
          linkedDocumentsCount={linkedDocumentsCount}
        />
        {content}
      </PageBody>
    </TabCountersContext.Provider>
  );
}
